// Provider-neutral storage client.
//
// The server hands back an upload descriptor — { driver, method, url, headers,
// fields, key } — and this layer dispatches on `method` alone. `driver` is
// carried for logging only; adding an object store that also speaks presigned
// PUT needs no change here.
//
// MIME type, object key and size limit are all decided server-side.
import Ajax from '../ajax.js';
import FileHelper from 'helpers/file_helper.js';
import putDriver from './drivers/put.js';
import formDriver from './drivers/form.js';
import { variantUrl } from './variants.js';
import { maxUploadSizeMB, publicUrlBase, writeDriver } from './config.js';

const { humanSize, fileExtension } = FileHelper;

// Same monotonic counter as helpers/utilities.js#uuid, inlined so the legacy
// layouts do not have to pull the whole helper (and Turbolinks) into their pack.
let uuidCounter = 0;
function uuid() {
  const now = Date.now();
  uuidCounter = uuidCounter === now ? uuidCounter + 1 : Math.max(now, uuidCounter) + 1;
  return uuidCounter;
}

// Transfers are slower than plain API calls, so they get their own budget
// instead of Ajax's 20s default.
const UPLOAD_TIMEOUT = 60 * 1000;

const DRIVERS = {
  PUT: putDriver,
  POST: formDriver
};

function driverFor(descriptor) {
  const driver = DRIVERS[String(descriptor.method || '').toUpperCase()];
  if (!driver) {
    throw new Error(`unsupported upload method: ${descriptor.method}`);
  }
  return driver;
}

function toQuery(params) {
  return Object.keys(params)
    .filter((key) => params[key] !== undefined && params[key] !== null && params[key] !== '')
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');
}

let Storage = (function () {
  class Storage {
    static get driver() {
      return writeDriver();
    }

    // Turns an object key into a public URL; full URLs pass through.
    static publicImageUrl(keyOrUrl, base = publicUrlBase()) {
      if (!keyOrUrl) return keyOrUrl;
      if (/^https?:\/\//.test(keyOrUrl)) return keyOrUrl;

      return `${base}/${keyOrUrl}`;
    }

    // `variant` is a neutral style name such as 'logo' or 'cover'.
    static cropImage(url, variant = '') {
      if (!url) return url;

      return variantUrl(url, variant);
    }

    // Asks the server for an upload descriptor. Filename and size go along so
    // the server can pin the Content-Type and reject oversized files up front.
    static getUploadCredentials(callback, { url = '/attachments/uptoken.json', file, customVariables } = {}) {
      const params = {};
      if (file) {
        params.filename = file.name;
        params.size = file.size;
        if (file.type) params.mime_type = file.type;
      }
      Object.keys(customVariables || {}).forEach((key) => {
        params[`custom_variables[${key}]`] = customVariables[key];
      });

      const query = toQuery(params);
      const requestUrl = query ? `${url}${url.indexOf('?') === -1 ? '?' : '&'}${query}` : url;

      return Ajax.get(requestUrl, {
        success(data) {
          return callback(data || {});
        }
      });
    }

    static Delete(attachmentId, { success, error, complete } = {}) {
      return Ajax.delete(`/attachments/${attachmentId}.json`, {
        success(data, xhr) {
          return typeof success === 'function' ? success(false, xhr) : void 0;
        },
        error(xhr) {
          return typeof error === 'function' ? error({ message: '删除失败' }, xhr) : void 0;
        },
        complete(xhr) {
          return typeof complete === 'function' ? complete(false, xhr) : void 0;
        }
      });
    }

    static getAttachmentDownloadUrl(id) {
      return `/attachments/${id}/download`;
    }

    static getMaterialDownloadUrl(id) {
      return `/material/files/${id}/download_url`;
    }

    // Durable href for html_body insert: proxy path, never a signed storage URL.
    static materialsForEditor(materials) {
      return (materials || []).map((item) => {
        if (!item || item.id == null || item.id === '') return item;

        return Object.assign({}, item, {
          download_url: this.getMaterialDownloadUrl(item.id)
        });
      });
    }

    static deleteAttachment(id, callbacks = {}) {
      return Ajax.delete(`/attachments/${id}.json`, {
        success(data, xhr) {
          return typeof callbacks.success === 'function' ? callbacks.success(data, 'success', xhr) : void 0;
        },
        error(xhr, status) {
          return typeof callbacks.error === 'function' ? callbacks.error(xhr, status) : void 0;
        },
        complete(xhr) {
          return typeof callbacks.complete === 'function' ? callbacks.complete(xhr, 'complete') : void 0;
        }
      });
    }

    // Uploads to the general-purpose bucket and hands back a public URL.
    static sendFile(file, callbacks = {}) {
      return new Storage.Image(file).upload({
        success(url, xhr) {
          return typeof callbacks.success === 'function' ? callbacks.success(url, 'success', xhr) : void 0;
        },
        error(err, xhr) {
          return typeof callbacks.error === 'function' ? callbacks.error(xhr, 'error', err && err.message) : void 0;
        },
        progress(_err, percentage) {
          return typeof callbacks.progress === 'function' ? callbacks.progress(percentage) : void 0;
        },
        complete(_err, xhr) {
          return typeof callbacks.complete === 'function' ? callbacks.complete(xhr, 'complete') : void 0;
        }
      });
    }

    static uploadAttachment(file, callbacks = {}, options = {}) {
      const attachment = new Storage.Attachment(
        file,
        _.extend({ limitSize: maxUploadSizeMB() * 1024 * 1024 }, options)
      );

      attachment.upload({
        success(_err, xhr) {
          return typeof callbacks.success === 'function'
            ? callbacks.success(attachment, 'success', xhr, file)
            : void 0;
        },
        error(err, xhr) {
          return typeof callbacks.error === 'function' ? callbacks.error(xhr, file, err && err.message) : void 0;
        },
        progress(_err, percentage) {
          return typeof callbacks.progress === 'function' ? callbacks.progress(percentage) : void 0;
        },
        complete(_err, xhr) {
          return typeof callbacks.complete === 'function' ? callbacks.complete(xhr, 'complete') : void 0;
        }
      });

      return attachment;
    }

    // Material library upload: same flow, different endpoints and extra fields.
    static uploadImage(file, options = {}) {
      const customVariables = {};
      if (options.folder_id != null) customVariables.folder_id = options.folder_id;
      if (options.shared != null) customVariables.shared = options.shared;

      const material = new Storage.Attachment(file, {
        uptokenUrl: '/admin/material/files/uptoken.json',
        createUrl: '/admin/material/files.json',
        custom_variables: customVariables,
        limitSize: maxUploadSizeMB() * 1024 * 1024
      });

      material.upload({
        success(_err, xhr) {
          return typeof options.success === 'function' ? options.success(material, 'success', xhr) : void 0;
        },
        error(err, xhr) {
          return typeof options.error === 'function' ? options.error(xhr, 'error', err && err.message) : void 0;
        },
        progress(_err, percentage) {
          return typeof options.progress === 'function' ? options.progress(percentage) : void 0;
        },
        // Fires once per file on both success and failure, so callers counting
        // completions cannot stall on a failed upload.
        complete(_err, xhr) {
          return typeof options.complete === 'function' ? options.complete(xhr, 'complete') : void 0;
        }
      });

      return material;
    }

    // Multi-file convenience wrapper used by the legacy Backbone field editor.
    static uploadAttachments(files, callbacks = {}) {
      const sources = Array.prototype.slice.call(files || []);
      const collection = new Storage.Attachments(sources);
      const sourceFor = (item) => sources[collection.files.indexOf(item)];

      collection.upload({
        success(item, _error, xhr) {
          return typeof callbacks.success === 'function'
            ? callbacks.success(item, 'success', xhr, sourceFor(item))
            : void 0;
        },
        error(item, err, xhr) {
          return typeof callbacks.error === 'function' ? callbacks.error(xhr, sourceFor(item), err) : void 0;
        },
        complete(item, xhr) {
          return typeof callbacks.complete === 'function' ? callbacks.complete(xhr, 'complete') : void 0;
        },
        uploadCompleted() {
          return typeof callbacks.allComplete === 'function' ? callbacks.allComplete() : void 0;
        }
      });

      return collection;
    }
  }

  Storage.states = {
    newRecord: 0,
    persisted: 1,
    destroyed: 2
  };

  return Storage;
})();

Storage.Image = class Image {
  constructor(attrs) {
    if (attrs instanceof File) {
      this.rawFile = attrs;
    }
    this.options = attrs;
  }

  upload({ success, error, complete, progress } = {}) {
    if (this.rawFile == null) return;

    return Storage.getUploadCredentials(
      (descriptor) => {
        if (!this.rawFile) return;

        const driver = driverFor(descriptor);
        this._uploadRequest = driver.upload(descriptor, this.rawFile, {
          timeout: UPLOAD_TIMEOUT,
          success: (body, xhr) => {
            const key = driver.createsRecord && body ? body.key : descriptor.key;
            const url = descriptor.public_url || Storage.publicImageUrl(key);
            return typeof success === 'function' ? success(url, xhr) : void 0;
          },
          error(xhr) {
            return typeof error === 'function' ? error({ message: '上传失败，请重试' }, xhr) : void 0;
          },
          progress(percentage) {
            return typeof progress === 'function' ? progress(false, percentage) : void 0;
          },
          complete: (xhr) => {
            this._uploadRequest = null;
            return typeof complete === 'function' ? complete(false, xhr) : void 0;
          }
        });
        return this._uploadRequest;
      },
      { url: '/uploads/uptoken.json', file: this.rawFile }
    );
  }
};

Storage.Attachment = class Attachment {
  static Delete(attachmentId, callbacks) {
    return Storage.Delete(attachmentId, callbacks);
  }

  constructor(attrs, options = {}) {
    if (attrs instanceof File) {
      this.rawFile = attrs;
    }
    this.options = options;
    this._init(attrs);
    this.state = this.id != null ? Storage.states.persisted : Storage.states.newRecord;
  }

  toJSON() {
    return _.pick(this, 'id', 'name', 'size', 'type');
  }

  _init(attrs) {
    _.extend(this, { id: null, _uploadRequest: null, _progress: 0, _error_message: '' }, attrs);
    if (this.type == null) {
      this.type = attrs.mime_type;
    }
    if (this.humanSize == null) {
      this.humanSize = humanSize(parseInt(this.size));
    }
    if (this.extension == null) {
      this.extension = fileExtension(this.type);
    }
    return this._uid != null ? this._uid : (this._uid = uuid());
  }

  upload({ success, error, progress, complete } = {}) {
    if (this.rawFile == null) return;

    if (this.rawFile.size > this.options.limitSize) {
      if (typeof error === 'function') error({ message: '文件尺寸过大' }, void 0);
      if (typeof complete === 'function') complete(false, void 0);
      return;
    }

    const finish = (record, xhr) => {
      if (record && record.id) {
        this.state = Storage.states.persisted;
        delete this.rawFile;
        record.type = record.mime_type;
        this._init(record);
        return typeof success === 'function' ? success(false, xhr) : void 0;
      }
      return typeof error === 'function' ? error({ message: '处理失败，请重试' }, xhr) : void 0;
    };

    return Storage.getUploadCredentials(
      (descriptor) => {
        if (!this.rawFile) return;

        const driver = driverFor(descriptor);
        const rawFile = this.rawFile;

        this._uploadRequest = driver.upload(descriptor, rawFile, {
          timeout: UPLOAD_TIMEOUT,
          success: (body, xhr) => {
            // A form upload has already been through the provider's signed
            // callback, so the response is the record itself.
            if (driver.createsRecord) {
              this._uploadRequest = null;
              return finish(body, xhr);
            }

            // The object is in storage but nothing is persisted yet. The server
            // re-reads it for size and MIME, so only the key and name go up.
            return Ajax.post(this.options.createUrl || '/attachments.json', {
              contentType: 'application/json',
              data: {
                key: descriptor.key,
                name: rawFile.name,
                size: rawFile.size,
                ...(this.options.custom_variables || {})
              },
              success: (record, createXhr) => finish(record, createXhr),
              error(createXhr) {
                return typeof error === 'function' ? error({ message: '处理失败，请重试' }, createXhr) : void 0;
              },
              complete: (createXhr) => {
                this._uploadRequest = null;
                return typeof complete === 'function' ? complete(false, createXhr) : void 0;
              }
            });
          },
          error(xhr) {
            return typeof error === 'function' ? error({ message: '上传失败，请重试' }, xhr) : void 0;
          },
          progress(percentage) {
            return typeof progress === 'function' ? progress(false, percentage) : void 0;
          },
          complete: (xhr) => {
            // The presigned driver still has the create request pending, so it
            // reports completion from there instead.
            if (!driver.createsRecord) return;

            this._uploadRequest = null;
            return typeof complete === 'function' ? complete(false, xhr) : void 0;
          }
        });
        return this._uploadRequest;
      },
      {
        url: this.options.uptokenUrl,
        file: this.rawFile,
        customVariables: this.options.custom_variables
      }
    );
  }

  cancelUpload() {
    if (this._uploadRequest) {
      this._uploadRequest.abort();
      return (this._uploadRequest = null);
    }
    // Still waiting on credentials — drop the file so the callback no-ops.
    return (this.rawFile = null);
  }

  delete({ success, error, complete } = {}) {
    switch (this.state) {
      case Storage.states.persisted:
        return this.constructor.Delete(this.id, {
          success: (err, xhr) => {
            this.state = Storage.states.destroyed;
            return typeof success === 'function' ? success(false, xhr) : void 0;
          },
          error(xhr) {
            return typeof error === 'function' ? error({ message: '删除失败' }, xhr) : void 0;
          },
          complete(xhr) {
            return typeof complete === 'function' ? complete(false, xhr) : void 0;
          }
        });
      case Storage.states.newRecord:
        this.cancelUpload();
        return typeof success === 'function' ? success(false, this._uploadRequest) : void 0;
      default:
        throw new Error('has been destroyed');
    }
  }
};

Storage.Attachments = class Attachments {
  constructor(files = [], options = {}) {
    this.counters = { uploading: 0, completed: 0 };
    this.uploadOptions = _.extend(
      { limitSize: maxUploadSizeMB() * 1024 * 1024, maxLimit: 2 },
      options
    );
    this.files = _.map(files, (file) => new Storage.Attachment(file, this.uploadOptions));
  }

  upload(options = {}) {
    this.uploadOptions = _.extend(this.uploadOptions, options);
    return this._uploadNext();
  }

  removeItem(file) {
    var base;
    this.files = _.filter(this.files, (item) => item !== file);
    if (file._uploadRequest) {
      // abort 会触发 xhr 的 error 事件(会执行error和complete回调)，这里需要禁止这个行为
      file._uploadRequest._error_callbacks = [];
      file._uploadRequest._complete_callbacks = [];
      file._uploadRequest.xhr.abort();
      file._uploadRequest = null;
      this.counters.uploading -= 1;
      if (this.counters.completed === this.files.length) {
        return typeof (base = this.uploadOptions).uploadCompleted === 'function' ? base.uploadCompleted() : void 0;
      }
      return this._uploadNext();
    }
    return (this.rawFile = null);
  }

  _uploadNext(options = this.uploadOptions) {
    if (!this.__canUpload(options.maxLimit)) return;

    const currentFile = this.files[this.counters.uploading + this.counters.completed];
    this.counters.uploading += 1;
    currentFile.upload({
      success(err, xhr) {
        return typeof options.success === 'function' ? options.success(currentFile, err, xhr) : void 0;
      },
      error(err, xhr) {
        currentFile._error_message = err.message;
        return typeof options.error === 'function' ? options.error(currentFile, err, xhr) : void 0;
      },
      progress(err, percentage) {
        currentFile._progress = percentage;
        return typeof options.progress === 'function' ? options.progress(currentFile, percentage) : void 0;
      },
      complete: (xhr) => {
        currentFile._progress = 0;
        if (typeof options.complete === 'function') {
          options.complete(currentFile, xhr);
        }
        this.counters.completed += 1;
        this.counters.uploading -= 1;
        if (this.counters.completed === this.files.length) {
          return typeof options.uploadCompleted === 'function' ? options.uploadCompleted() : void 0;
        }
        return this._uploadNext();
      }
    });
    return this._uploadNext();
  }

  __canUpload(maxLimit) {
    return (
      this.counters.uploading < maxLimit &&
      this.counters.uploading + this.counters.completed < this.files.length
    );
  }
};

export default Storage;
